<template>
  <div class="student-hours-tab">
    <div v-if="loading" class="loading-block">
      <van-loading type="spinner" />
    </div>

    <template v-else>

      <div v-if="courseItems.length" class="card-list">
        <div v-for="item in courseItems" :key="item.key" class="record-card">
          <div class="record-top">
            <div class="record-title">{{ item.title }}</div>
          </div>
          <div class="record-meta">
            <span v-for="meta in item.metaItems" :key="meta" class="meta-chip">{{ meta }}</span>
          </div>
        </div>
      </div>
      <div v-else class="empty-block">暂无课时信息</div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { showToast } from 'vant'
import { getStudentCourseList } from '@/api/crm'

const props = defineProps({
  studentId: {
    type: [String, Number],
    default: ''
  }
})

const loading = ref(false)
const courseList = ref([])

const summaryText = computed(() => `${formatNumber(courseList.value.reduce((total, item) => total + toNumber(item.remainingClassHour), 0))} 课时`)
const courseItems = computed(() => courseList.value.map((item, index) => ({
  key: item.classHourId || item.courseId || `${item.courseName || 'course'}-${index}`,
  title: item.courseName || '-',
  metaItems: [
    `剩余购买 ${formatNumber(item.consumableBuyClassHourCount)}`,
    `剩余赠送 ${formatNumber(item.consumableGiftClassHourCount)}`
  ]
})))

watch(() => props.studentId, async studentId => {
  courseList.value = []
  if (!studentId) {
    return
  }
  loading.value = true
  try {
    const result = await getStudentCourseList(studentId)
    courseList.value = Array.isArray(result) ? result : []
  } catch (error) {
    showToast(error?.message || '加载课时信息失败')
  } finally {
    loading.value = false
  }
}, { immediate: true })

function toNumber(value) {
  const number = Number(value || 0)
  return Number.isNaN(number) ? 0 : number
}

function formatNumber(value) {
  const number = toNumber(value)
  return Number.isInteger(number) ? String(number) : number.toFixed(2)
}
</script>

<style scoped>
.student-hours-tab {
  padding-bottom: 8px;
}

.loading-block,
.empty-block {
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
}

.summary-strip {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 18px;
  background: linear-gradient(135deg, #eff6ff 0%, #ffffff 100%);
  border: 1px solid rgba(191, 219, 254, 0.8);
  margin-bottom: 14px;
}

.summary-label {
  font-size: 13px;
  color: #64748b;
}

.summary-value {
  font-size: 22px;
  font-weight: 700;
  color: #1d4ed8;
  white-space: nowrap;
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.record-card {
  border-radius: 18px;
  background: #fff;
  border: none;
  padding: 15px 16px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
}

.record-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.record-title {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}

.record-side {
  font-size: 14px;
  font-weight: 600;
  color: #1d4ed8;
  white-space: nowrap;
}

.record-meta {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 13px;
  color: #64748b;
  background: #f8fafc;
  border: none;
}

.empty-block {
  border-radius: 14px;
  background: #f8fafc;
  border: 1px dashed #dbe4ee;
}

@media (max-width: 480px) {
  .summary-strip,
  .record-top {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
